import * as React from "react";

import { cn } from "@/lib/cn";

/**
 * `Table` is wrapped in a horizontal scroll container by default so wide
 * tables degrade gracefully on narrow screens instead of breaking the page
 * layout. The wrapper carries the `data-tabular` attribute so the
 * `tabular-nums` rule in `globals.css` still applies.
 *
 * Use `<Table containerClassName="…">` to customise the wrapper, e.g. to
 * remove the scroll container entirely when the consumer already manages it.
 */
export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <div
      className={cn("w-full overflow-x-auto", containerClassName)}
      data-tabular
    >
      <table
        ref={ref}
        className={cn("w-full border-collapse text-sm", className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

export const Thead = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  // A real header BAND (full muted fill) + a full-strength hairline. The
  // band sits clearly ABOVE the rows (which stay on the panel surface) so
  // header-vs-body contrast reads in both light and dark — the fill, not
  // the text weight, is what separates the header from the data.
  <thead className={cn("bg-muted border-b border-border", className)} {...props} />
);

export const Tbody = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody className={className} {...props} />
);

export const Tr = ({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
  // Full-strength dividers (was border/60 → too faint) + a clearer hover.
  <tr
    className={cn(
      "border-b border-border last:border-0 transition-colors hover:bg-muted/50",
      className,
    )}
    {...props}
  />
);

export const Th = ({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  // Column labels read as a HEADER: small + uppercase + tracked, semibold
  // on the muted band. Uppercase signals "header" while the band carries
  // the contrast against the rows.
  <th
    className={cn(
      "h-9 px-3 text-left align-middle text-[11px] font-semibold uppercase tracking-wide text-foreground/75 whitespace-nowrap",
      className,
    )}
    {...props}
  />
);

export const Td = ({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  // Taller rows (h-10 vs h-9) give the table room to breathe — one of
  // the cheapest "feels expensive" wins.
  <td
    className={cn("h-10 px-3 align-middle text-foreground", className)}
    {...props}
  />
);
